import * as Vue from 'vue';
import { store } from '../store/store';

/**
 * The page-ready marker, for automated tests and tooling to wait on instead of guessing with time.
 *
 * A page calls enterPage(route) when it is entered — at the start of the hook that loads its data, before any await —
 * and .ready() on what that returns once it has its data and will stay. When no load is in flight either
 * (store.state.loadingCount, which also drives PageOverlay) and the DOM has updated, body gets data-ready="<route>".
 * It is an attribute, not an event, so a test can read it whenever it looks — before or after the page became ready.
 *
 * Values a test needs next (a request id the page got back, the channel a code went to) ride along in
 * body[data-ready-values], a JSON object keyed by flow: ready(values) fills the "ready" flow with the marker, and
 * set(flow, values) fills any other flow whenever it happens — e.g. "retrySend" when the user asks for a new code.
 * Values are flat (string, number, boolean). The page decides what it exposes: everything here is readable by any
 * script on the page and lands in test evidence and traces, so expose only what a test needs next.
 *
 * Every navigation clears both attributes first: a router navigation (clearReady, from the router) and a full-page one
 * (window.location, which bypasses the router — beforeunload). A page that navigates away before its load finishes (a
 * redirect on 401, an error page, an auto-submit) never marks the next page with its own name or values: its ready()
 * and set() do nothing.
 */
export type ReadyValues = Record<string, string | number | boolean>;

let navigations = 0;
let pending: Vue.WatchStopHandle | null = null;
let flows: Record<string, ReadyValues> = {};

const stopPending = () => {
  pending?.();
  pending = null;
};

function writeValues() {
  if (Object.keys(flows).length) document.body.dataset.readyValues = JSON.stringify(flows);
  else delete document.body.dataset.readyValues;
}

/** The values that are present (an empty, null, or undefined value is left out). */
function presentValues(values: ReadyValues | undefined): ReadyValues {
  return Object.fromEntries(Object.entries(values ?? {}).filter(([, value]) => value !== undefined && value !== null && value !== ''));
}

function signal(route: string, values: ReadyValues) {
  stopPending();
  const mark = () =>
    Vue.nextTick(() => {
      if (Object.keys(values).length) flows.ready = values;
      writeValues();
      document.body.dataset.ready = route;
    });
  if (store.state.loadingCount <= 0) {
    mark();
    return;
  }
  pending = Vue.watch(
    () => store.state.loadingCount <= 0,
    (idle) => {
      if (!idle) return;
      stopPending();
      mark();
    },
  );
}

export function enterPage(route: string): { ready(values?: ReadyValues): void; set(flow: string, values: ReadyValues): void } {
  const entered = navigations;
  const current = () => navigations === entered;
  return {
    ready(values) {
      if (current()) signal(route, presentValues(values));
    },
    set(flow, values) {
      if (!current()) return;
      const kept = presentValues(values);
      Vue.nextTick(() => {
        if (!current()) return;
        flows[flow] = kept;
        writeValues();
      });
    },
  };
}

export function clearReady() {
  navigations++;
  stopPending();
  flows = {};
  delete document.body.dataset.ready;
  delete document.body.dataset.readyValues;
}

window.addEventListener('beforeunload', clearReady);
